// src/helpers/Api.js
import axios from 'axios';

const BASE_URL = 'https://dashboard.gestlifesurrogacy.com/api/page/';

// Solo importamos fs y path en el entorno de Node.js (no en el navegador)
let fs = undefined;
let path = undefined;
let JSON_DATA_DIR = undefined;

// Solo se ejecutará en Node.js, no en el navegador
if (typeof window === 'undefined') {
  try {
    fs = require('fs');
    path = require('path');
    JSON_DATA_DIR = path.join(process.cwd(), 'public', 'data');
    
    // Verificamos si estamos en entorno de build
    const IS_BUILD_TIME = process.env.NEXT_PHASE === 'phase-production-build' || process.env.NODE_ENV === 'build';
    
    // Asegurar que el directorio de datos exista durante el build
    if (IS_BUILD_TIME && !fs.existsSync(JSON_DATA_DIR)) {
      fs.mkdirSync(JSON_DATA_DIR, { recursive: true });
    }
  } catch (error) {
    console.warn('fs/path modules not available, running in browser mode');
  }
}

// Helper para escribir datos en archivos JSON durante el build
const saveDataToJson = (filename, data) => {
  if (typeof window !== 'undefined' || !fs || !path || !JSON_DATA_DIR) return;
  
  try {
    const filePath = path.join(JSON_DATA_DIR, filename);
    fs.writeFileSync(filePath, JSON.stringify(data, null, 2));
    console.log(`Data saved to ${filePath}`);
  } catch (error) {
    console.error(`Error saving data to ${filename}`, error);
  }
};

// Helper para leer datos desde archivos JSON en el cliente
const readDataFromJson = async (filename) => {
  if (typeof window === 'undefined') return null;
  
  try {
    const response = await fetch(`/data/${filename}`);
    if (!response.ok) throw new Error('Failed to fetch data');
    return await response.json();
  } catch (error) {
    console.error(`Error reading ${filename}`, error);
    return null;
  }
};

export const getPages = async () => {
  try {
    // En el cliente, intentar cargar datos desde JSON estático
    if (typeof window !== 'undefined') {
      const cached = await readDataFromJson('pages.json');
      if (cached) return cached;
    }

    // En tiempo de build (servidor Node.js), obtener datos de la API y guardarlos
    if (typeof window === 'undefined' && fs && path) {
      const response = await axios.get(BASE_URL);
      saveDataToJson('pages.json', response.data);
      return response.data;
    }
    
    // Si no hay datos cacheados o no estamos en el cliente, usar la API
    const response = await axios.get(BASE_URL);
    return response.data;
  } catch (error) {
    console.error('Error fetching pages', error);
    throw error;
  }
};

export const getPageBySlug = async (slug) => {
  try {
    // Asegurarse de que el slug esté en el formato correcto
    const normalizedSlug = slug.startsWith('/') ? slug.slice(1) : slug;
    const safeSlug = normalizedSlug.replace(/\//g, '_') || 'home';
    // Evitar llamada a API para página 404
    if (normalizedSlug === '404') return null;
    const jsonFilename = `page_${safeSlug}.json`;
    
    // Intentar cargar desde JSON estático en cliente
    if (typeof window !== 'undefined') {
      const cached = await readDataFromJson(jsonFilename);
      if (cached !== null) return cached;
    }

    // En tiempo de build (servidor Node.js), obtener datos de la API y guardarlos
    if (typeof window === 'undefined' && fs && path) {
      try {
        const apiUrl = `${BASE_URL}${normalizedSlug}`;
        const response = await axios.get(apiUrl);
        saveDataToJson(jsonFilename, response.data);
        return response.data;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          saveDataToJson(jsonFilename, null);
          return null;
        }
        throw error;
      }
    }
    
    // Si no hay datos cacheados o no estamos en el cliente, usar la API
    const apiUrl = `${BASE_URL}${normalizedSlug}`;
    try {
      const response = await axios.get(apiUrl);
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return null;
      }
      console.error(`Error fetching page with slug ${slug}`, error);
      throw error;
    }
  } catch (error) {
    console.error(`Error with page slug ${slug}`, error);
    throw error;
  }
};

// Función para pre-construir todos los datos durante el build
export const prebuildAllPages = async () => {
  // Solo ejecutar en Node.js, no en el navegador
  if (typeof window !== 'undefined' || !fs || !path) return;
  
  try {
    console.log('Pre-building all pages...');
    const pages = await getPages();
    
    // Pre-build de la página principal
    await getPageBySlug('');
    console.log('Pre-built home page');
    
    // Si hay una lista de páginas, construir cada una
    if (pages && Array.isArray(pages)) {
      for (const page of pages) {
        if (page.slug) {
          await getPageBySlug(page.slug);
          console.log(`Pre-built page: ${page.slug}`);
        }
      }
    }
    
    console.log('All pages pre-built successfully!');
    return pages;
  } catch (error) {
    console.error('Error pre-building pages:', error);
    throw error;
  }
};
